import React, { useState, useEffect } from 'react';
import { Modal, Form, InputNumber, Select, Button, message, notification, Space, Input } from 'antd';
import { DoubleRightOutlined } from '@ant-design/icons';
import axios from 'axios';
import {production} from "../../API/API";

const { Option } = Select;

const TransferModal = ({ visible, onClose, item, stores, onTransferSuccess, username }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [selectedFromStore, setSelectedFromStore] = useState(null);
    const [availableQuantity, setAvailableQuantity] = useState(0);

    // Проверьте, что item и stores доступны, иначе используйте пустые значения
    const validItem = item || {};
    const validStores = Array.isArray(stores) ? stores : [];

    // Получаем склады с количеством > 0 из item (ключи начинаются с 'store_')
    const fromStoreKeys = Object.keys(validItem).filter(
        key => key.startsWith('store_') && validItem[key] > 0
    );

    // Список складов отправления: у которых есть товар, enable = '1' и type ≠ '4'
    const availableFromStores = validStores.filter(store => {
        const storeKey = `store_${store.name_db}`;
        return fromStoreKeys.includes(storeKey) &&
            store.enable === '1' &&
            store.type !== '4';
    });

    // Склады назначения: все кроме выбранного склада отправления, enable = '1'
    const availableToStores = validStores.filter(store => {
        const storeKey = `store_${store.name_db}`;
        return storeKey !== selectedFromStore &&
            store.enable === '1';
    });

    // Обработчик изменения склада отправления
    const handleFromStoreChange = (value) => {
        setSelectedFromStore(value);
        setAvailableQuantity(Number(validItem[value]) || 0);

        // Сбрасываем склад назначения и количество
        form.setFieldsValue({
            to_store: undefined,
            quantity: Math.min(1, Number(validItem[value]) || 0)
        });
    };

    // Сбрасываем состояние при закрытии модального окна
    useEffect(() => {
        if (!visible) {
            setSelectedFromStore(null);
            setAvailableQuantity(0);
            form.resetFields();
        }
    }, [visible, form]);

    const handleSubmit = async (values) => {
        if (!selectedFromStore) {
            message.error('Выберите склад отправления');
            return;
        }

        setLoading(true);

        try {
            const formData = new FormData();
            formData.append('item_id', validItem.id);
            // Убираем префикс 'store_' при отправке на сервер
            formData.append('from_store', selectedFromStore);
            formData.append('to_store', values.to_store);
            formData.append('quantity', values.quantity);
            formData.append('owner', username);
            formData.append('description', values.description || '');
            formData.append('date', new Date().getTime().toString());
            const response = await axios.post(`https://gdapp.org/API/${production ? '4.0' : '4.0_test'}/store/post_transfer.php`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                withCredentials: true
            });

            if (response.data.success) {
                notification.success({
                    message: 'Перемещение выполнено',
                    description: `Товар "${validItem.name}" успешно перемещен`,
                    placement: 'topRight',
                });

                onTransferSuccess(response.data);
                form.resetFields();
                setSelectedFromStore(null);
                setAvailableQuantity(0);
                onClose();
            } else {
                message.error(response.data.error || 'Ошибка при перемещении');
            }
        } catch (error) {
            console.error('Transfer error:', error);
            message.error(error.response?.data?.error || 'Ошибка при перемещения');
        } finally {
            setLoading(false);
        }
    };

    const fromStoreName = selectedFromStore
        ? validStores.find(s => `store_${s.name_db}` === selectedFromStore)?.name || 'Неизвестный склад'
        : 'Не выбран';

    if (!visible) {
        return null;
    }

    return (
        <Modal
            title={
                <Space>
                    <DoubleRightOutlined />
                    Перемещение товара
                </Space>
            }
            open={visible}
            onCancel={onClose}
            footer={null}
            width={500}
        >
            <div style={{ marginBottom: 16 }}>
                <strong>Товар:</strong> {validItem.name || ''}
                <br />
                <strong>Склад отправления:</strong> {fromStoreName}
                <br />
                {selectedFromStore && (
                    <>
                        <strong>Доступно на складе:</strong>
                        {` ${availableQuantity} ${validItem.package_name || ''}`}
                    </>
                )}
            </div>

            <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{
                    owner: 'Система',
                }}
            >
                <Form.Item
                    name="from_store"
                    label="С склада"
                    rules={[{ required: true, message: 'Выберите склад отправления' }]}
                >
                    <Select
                        placeholder="Выберите склад отправления"
                        onChange={handleFromStoreChange}
                        value={selectedFromStore}
                    >
                        {availableFromStores.map(store => {
                            const storeKey = `store_${store.name_db}`;
                            return (
                                <Option key={storeKey} value={storeKey}>
                                    {store.name} ({validItem[storeKey]} {'шт.'})
                                </Option>
                            );
                        })}
                    </Select>
                </Form.Item>

                {selectedFromStore && (
                    <>
                        <Form.Item
                            name="to_store"
                            label="На склад"
                            rules={[{ required: true, message: 'Выберите склад назначения' }]}
                        >
                            <Select placeholder="Выберите склад назначения">
                                {availableToStores.map(store => {
                                    const storeKey = `store_${store.name_db}`;
                                    return (
                                        <Option key={storeKey} value={storeKey}>
                                            {store.name} ({validItem[storeKey]} {'шт.'})
                                        </Option>
                                    );
                                })}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="quantity"
                            label="Количество"
                            rules={[
                                { required: true, message: 'Введите количество' },
                                {
                                    type: 'number',
                                    min: 0.01,
                                    max: availableQuantity,
                                    message: `Количество должно быть от 0.01 до ${availableQuantity}`,
                                },
                            ]}
                        >
                            <InputNumber
                                min={0.01}
                                max={availableQuantity}
                                step={0.01}
                                style={{ width: '100%' }}
                                placeholder="Введите количество"
                            />
                        </Form.Item>


                        <Form.Item name="description" label="Примечание">
                            <Input.TextArea rows={3} placeholder="Дополнительная информация" />
                        </Form.Item>
                    </>
                )}

                <Form.Item>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        style={{ marginRight: 8 }}
                        disabled={!selectedFromStore}
                    >
                        Переместить
                    </Button>
                    <Button onClick={onClose}>Отмена</Button>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default TransferModal;